/**
 * @author Charles Carlson
 * @copyright Copyright 2026 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2026-08-18
 * @description Implementation of consent policy interface, matching region to consent policy.
 * @filename gtag-consent-policy.ts
 */

import { EU_COUNTRY_CODES } from "../constants/eu-country-codes";

type ConsentStatus = "granted" | "denied";

interface ConsentSignals {
  ad_storage: ConsentStatus;
  analytics_storage: ConsentStatus;
  ad_user_data: ConsentStatus;
  ad_personalization: ConsentStatus;
}

//ConsentPolicy is the interface that is implemented by different region types
interface ConsentPolicy {
  readonly regionCode: string;
  readonly requiresBanner: boolean;
  getDefaults(): ConsentSignals;
}

//Implicit Deny, requiring a banner for a user to consent for analytic tracking in the EU/UK regions
class GDPRConsentPolicy implements ConsentPolicy {
  readonly requiresBanner: boolean = true;
  constructor(readonly regionCode: string) {}
  getDefaults(): ConsentSignals {
    return {
      ad_storage: "denied",
      analytics_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    };
  }
}
//US, and CA have implicit granted, however a user can turn off tracking if they choose to do so
//Notice for Quebec, holds closer consent laws similar to GDPR
class CCPAConsentPolicy implements ConsentPolicy {
  readonly requiresBanner: boolean = false;
  constructor(readonly regionCode: string) {}
  getDefaults(): ConsentSignals {
    return {
      ad_storage: "granted",
      analytics_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    };
  }
}

//Default is to show banner and implicity deny, as it is the safest options
//for when Cloud front cannot determine a region
class DefaultConsentPolicy implements ConsentPolicy {
  readonly requiresBanner = true;

  constructor(readonly regionCode: string = "DEFAULT") {}

  getDefaults(): ConsentSignals {
    return {
      ad_storage: "denied",
      analytics_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    };
  }
}
//Policy registry that attributes region to what type of policy that requries
//Composite "COUNTRY-SUBDIVISION" keys let a province/state override its country's
//default - e.g. Quebec's Law 25 requires opt-in like GDPR, unlike the rest of Canada.
const POLICY_REGISTRY: Record<string, ConsentPolicy> = {
  GB: new GDPRConsentPolicy("GB"),
  US: new CCPAConsentPolicy("US"),
  CA: new CCPAConsentPolicy("CA"),
  "CA-QC": new GDPRConsentPolicy("CA-QC"),
  ...Object.fromEntries(EU_COUNTRY_CODES.map((code) => [code, new GDPRConsentPolicy(code)])),
};
//
/**
 * @return Policy object depending on the country and, if given, subdivision (e.g. "QC").
 * Checks the composite "COUNTRY-SUBDIVISION" key first, then falls back to the
 * country-level policy, then to the default if the country is unrecognized.
 **/
function getPolicyForRegion(country: string, subdivision?: string | null) {
  if (subdivision) {
    const compositePolicy = POLICY_REGISTRY[`${country}-${subdivision}`];
    if (compositePolicy) {
      return compositePolicy;
    }
  }

  return POLICY_REGISTRY[country] ?? new DefaultConsentPolicy(country);
}
export { ConsentPolicy, ConsentSignals, ConsentStatus, getPolicyForRegion, POLICY_REGISTRY };
